import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  makeStyles,
  Spinner,
  Text,
  tokens
} from "@fluentui/react-components";
import React from "react";
import { useIntl } from "react-intl";

import { useCommand } from "@tdp/core";

import { IAppSummary } from "../abstractions/IAppSummary";

const useStyles = makeStyles({
  container: {
    margin: "12px 0px 24px 0px",
    display: "grid",
    gap: "18px",
  },
  field: {
    display: "grid",
    justifyItems: "start",
  },
  input: {
    width: "85%",
  },
  errorText: {
    color: tokens.colorPaletteRedForeground1,
    marginTop: tokens.spacingVerticalXXS,
  }
});

interface IFormData {
  shortName: string;
  validationError: string;
}

export const AppListDuplicateDialog: React.FC<{
  sourceApp: Partial<IAppSummary> | null;
  isOpen: boolean;
  setOpen: (isOpen: boolean) => void;
}> = ({
  sourceApp,
  isOpen,
  setOpen
}) => {
    const classes = useStyles();
    const intl = useIntl();

    const duplicateAppCommand = useCommand((commands) => commands.appListCommands.duplicateApp);

    const [formData, setFormData] = React.useState<IFormData>({ shortName: "", validationError: "" });
    const [isProcessing, setIsProcessing] = React.useState<boolean>(false);

    const validateShortName = (value: string): string | null => {
      if (!value.trim()) {
        return intl.formatMessage({ defaultMessage: "Short name is required" });
      }
      if (value.length > 64) {
        return intl.formatMessage({ defaultMessage: "Short name must be 64 characters or less" });
      }
      return null;
    };

    const resetForm = () => {
      setFormData({ shortName: "", validationError: "" });
      setIsProcessing(false);
    };

    const handleClose = () => {
      resetForm();
      setOpen(false);
    };

    const handleDuplicate = () => {
      if (sourceApp?.appId && !formData.validationError && formData.shortName) {
        setIsProcessing(true);
        duplicateAppCommand({
          appId: sourceApp.appId,
          shortName: formData.shortName,
          onSuccess: () => {
            handleClose();
          },
          onFailure: () => {
            handleClose();
          }
        });
      }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      const validationError = validateShortName(value);
      setFormData({
        shortName: value,
        validationError: validationError ?? ""
      });
    };

    const isDuplicateDisabled = !!formData.validationError || !formData.shortName || !sourceApp || isProcessing;

    return (
      <Dialog open={isOpen}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>
              {intl.formatMessage({ defaultMessage: "Duplicate app" })}
            </DialogTitle>
            <DialogContent className={classes.container}>
              <div className={classes.field}>
                <Field
                  id={"appShortName"}
                  label={intl.formatMessage({ defaultMessage: "Short name" })}
                  required
                />
                <Input
                  aria-labelledby={"appShortName"}
                  className={classes.input}
                  autoFocus
                  value={formData.shortName}
                  onChange={handleInputChange}
                  placeholder={intl.formatMessage({
                    defaultMessage: "Enter the app short name"
                  })}
                  disabled={isProcessing}
                />
                <Text className={classes.errorText}>
                  {formData.validationError && formData.validationError
                  }
                </Text>
              </div>
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                onClick={handleClose}
                disabled={isProcessing}
              >
                {intl.formatMessage({ defaultMessage: "Cancel" })}
              </Button>
              <Button
                icon={isProcessing ? <Spinner size="tiny" /> : null}
                appearance="primary"
                disabled={isDuplicateDisabled}
                onClick={handleDuplicate}
              >
                {intl.formatMessage({ defaultMessage: "Duplicate" })}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    );
  };